import React, { type Key } from 'react';
import { BiDotsHorizontal, BiTrash } from 'react-icons/bi';
import { iconClasses } from '@/config/job';
import { Description } from '@/core/molecules/Comment/Description';
import { ActionDropdown, Confirm, cn } from '@/core/ui';
import type { RecordReview } from '@/graphql/gql/graphql';

type ReviewActionItem = {
  key: string;
  className?: string;
  startContent: React.ReactNode;
  title: string;
  color?: 'default' | 'danger' | 'warning' | 'secondary';
  operation: string;
  type: 'success' | 'danger' | 'warn' | 'info' | 'default';
  description: string;
};

const REVIEW_ACTIONS: Record<string, ReviewActionItem> = {
  DELETE: {
    key: 'delete',
    className: 'text-xs py-1',
    startContent: <BiTrash className={cn(iconClasses, 'text-danger-300')} />,
    title: 'Delete',
    color: 'danger',
    operation: 'delete',
    type: 'danger',
    description: 'Are you sure you want to delete this review?',
  },
};

export const ReviewActions = ({
  review,
  onAction,
  isLoading,
  className,
}: {
  review: RecordReview;
  onAction: (reviewId: string | number | null | undefined, operation: string) => void;
  isLoading: boolean;
  className?: string;
}) => {
  const [selectedAction, setSelectedAction] = React.useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);

  const actionItems = React.useMemo(() => {
    return Object.values(REVIEW_ACTIONS).map((action) => ({
      key: action.key,
      className: action.className,
      startContent: action.startContent,
      title: action.title,
      color: action.color,
    }));
  }, []);

  const handleAction = (key: Key) => {
    const action = Object.values(REVIEW_ACTIONS).find((a) => a.key === key);
    if (action) {
      setSelectedAction(action.operation);
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    if (selectedAction) {
      onAction(review.id, selectedAction);
      setIsConfirmOpen(false);
      setSelectedAction(null);
    }
  };

  const currentAction = selectedAction
    ? Object.values(REVIEW_ACTIONS).find((a) => a.operation === selectedAction)
    : null;

  if (actionItems.length === 0) {
    return null;
  }

  return (
    <>
      <ActionDropdown
        offset={0}
        classNames={{
          content: 'shadow-sm border border-default-200 rounded-md min-w-[130px]',
        }}
        onAction={handleAction}
        items={actionItems}
        className={className}
      >
        <div className='hover:bg-content2 flex justify-center items-center rounded-md px-1 py-0.5 cursor-pointer text-default-400'>
          <BiDotsHorizontal className='size-5 cursor-pointer text-default-400' />
        </div>
      </ActionDropdown>

      {currentAction && (
        <Confirm
          type={currentAction.type}
          title={`${currentAction.title} Review`}
          isLoading={isLoading}
          isOpen={isConfirmOpen}
          onOpenChange={setIsConfirmOpen}
          message={
            <Description
              comment={review.reason || ''}
              replyCount={0}
              type='delete'
              description={currentAction.description}
            />
          }
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
